import React, { useState, useRef } from 'react';
import { X, Upload, FileJson, FileSpreadsheet, Download, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { adminVehiclesAPI } from '../services/api';

const VehicleImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('add');
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setResult(null);

    try {
      const text = await selectedFile.text();
      let vehicles = [];

      if (selectedFile.name.endsWith('.json')) {
        const data = JSON.parse(text);
        vehicles = Array.isArray(data) ? data : data.vehicles || [];
      } else if (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.txt')) {
        vehicles = parseCSV(text);
      } else {
        throw new Error('Format non supporté (JSON ou CSV uniquement)');
      }
      if (vehicles.length === 0) {
        throw new Error('Aucun véhicule valide trouvé dans le fichier');
      }

      setPreviewData({
        filename: selectedFile.name,
        count: vehicles.length,
        enginesCount: vehicles.reduce((sum, v) => sum + countEngines(v.engines), 0),
        vehicles: vehicles.slice(0, 5), // Preview first 5
        allVehicles: vehicles
      });
    } catch (error) {
      setPreviewData(null);
      setResult({
        success: false,
        message: `Erreur de lecture: ${error.message}`
      });
    }
  };

  const countEngines = (engines) => {
    if (Array.isArray(engines)) return engines.length;
    if (engines && typeof engines === 'object') return Object.values(engines).flat().length;
    return 0;
  };

  const parseCSV = (text) => {
    const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];

    const delimiter = [';', ',', '\t'].reduce((best, d) =>
      lines[0].split(d).length > lines[0].split(best).length ? d : best, ';');
    const splitLine = (line) => {
      const out = []; let cur = ''; let q = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
        else if (ch === delimiter && !q) { out.push(cur); cur = ''; }
        else cur += ch;
      }
      out.push(cur);
      return out.map((v) => v.trim());
    };
    const clean = (v) => (v || '').trim();
    const headers = splitLine(lines[0]).map(h => clean(h).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''));

    const col = (...names) => {
      const idx = headers.findIndex(h => names.includes(h));
      return idx === -1 ? null : idx;
    };
    const cols = {
      brand: col('brand', 'marque'),
      model: col('model', 'modele'),
      years: col('years', 'annees', 'annee', 'years_range'),
      period: col('period', 'periode'),
      fuel: col('fuel', 'carburant', 'energie'),
      name: col('engine', 'moteur', 'name', 'nom', 'motorisation', 'version'),
      displacement: col('displacement', 'cylindree'),
      power: col('power', 'power_hp', 'puissance', 'ch', 'puissance_ch'),
      power_kw: col('power_kw', 'kw', 'puissance_kw'),
      engine_code: col('engine_code', 'code', 'code_moteur'),
    };
    if (cols.brand === null || cols.model === null) {
      throw new Error('Colonnes "marque" (brand) et "modele" (model) obligatoires dans l\'en-tête CSV');
    }

    const byKey = new Map();
    for (let i = 1; i < lines.length; i++) {
      const values = splitLine(lines[i]);
      const get = (c) => (c === null ? '' : values[c] || '');
      const brand = get(cols.brand).toUpperCase();
      const model = get(cols.model);
      if (!brand || !model) continue;

      const key = `${brand}|${model}`;
      if (!byKey.has(key)) {
        byKey.set(key, { brand, model, years: get(cols.years), engines: [] });
      }
      const vehicle = byKey.get(key);
      if (!vehicle.years && get(cols.years)) vehicle.years = get(cols.years);

      const name = get(cols.name);
      if (name || get(cols.power)) {
        vehicle.engines.push({
          period: get(cols.period) || vehicle.years || undefined,
          fuel: get(cols.fuel) || 'essence',
          name: name || `${get(cols.displacement)} ${get(cols.power)}ch`.trim(),
          displacement: parseInt(get(cols.displacement)) || 0,
          power: parseInt(get(cols.power)) || 0,
          power_kw: parseInt(get(cols.power_kw)) || undefined,
          engine_code: get(cols.engine_code),
        });
      }
    }
    return Array.from(byKey.values());
  };

  const handleImport = async () => {
    if (!previewData?.allVehicles?.length) return;

    setIsImporting(true);
    setResult(null);

    try {
      const response = await adminVehiclesAPI.importVehicles(previewData.allVehicles, importMode);
      setResult({
        success: true,
        message: response.message,
        imported: response.imported,
        updated: response.updated,
        errors: response.errors
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Erreur lors de l\'import'
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const data = await adminVehiclesAPI.exportVehicles();
      const blob = new Blob([JSON.stringify(data.vehicles, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vehicles_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('Erreur lors de l\'export');
    }
  };

  const downloadTemplate = (format) => {
    let content, filename, type;

    if (format === 'json') {
      const template = [
        {
          "brand": "RENAULT",
          "model": "Clio V",
          "years": [2019, 2020, 2021, 2022, 2023, 2024],
          "engines": {
            "2019-2024": [
              { "fuel": "essence", "displacement": 999, "power": 100, "power_kw": 74, "engine_code": "H5H 490", "name": "1.0 TCe 100" },
              { "fuel": "diesel", "displacement": 1461, "power": 85, "power_kw": 63, "engine_code": "K9K 872", "name": "1.5 Blue dCi 85" }
            ]
          }
        }
      ];
      content = JSON.stringify(template, null, 2);
      filename = 'template_vehicles.json';
      type = 'application/json';
    } else {
      content = [
        'marque;modele;annees;periode;carburant;motorisation;cylindree;puissance;kw;code_moteur',
        'RENAULT;Clio V;2019-;2019-;essence;1.0 TCe 100;999;100;74;H5H 490',
        'RENAULT;Clio V;2019-;2019-;diesel;1.5 Blue dCi 85;1461;85;63;K9K 872',
        'RENAULT;Clio V;2019-;2020-;hybride;E-Tech 140;1598;140;103;H4M',
        'PEUGEOT;208 II;2019-2024;2019-2024;diesel;1.5 BlueHDi 100;1499;100;75;DV5RD',
        'PEUGEOT;208 II;2019-2024;2019-2024;électrique;e-208 136;0;136;100;',
        'DACIA;Sandero III;2021-;;;;;;;'
      ].join('\n');
      filename = 'modele_vehicules.csv';
      type = 'text/csv;charset=utf-8';
      content = '\uFEFF' + content;
    }

    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-orange-50 to-orange-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Importer des véhicules</h2>
            <p className="text-sm text-gray-600 mt-1">Importez des véhicules depuis un fichier JSON ou CSV</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-full transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Templates */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Télécharger un modèle :</p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadTemplate('json')}
                className="flex items-center gap-2"
              >
                <FileJson size={16} className="text-blue-500" />
                Template JSON
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadTemplate('csv')}
                className="flex items-center gap-2"
              >
                <FileSpreadsheet size={16} className="text-green-500" />
                Template CSV
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => adminVehiclesAPI.exportVehiclesCsv()}
                className="flex items-center gap-2 ml-auto"
                data-testid="vehicles-export-csv"
              >
                <FileSpreadsheet size={16} className="text-green-500" />
                Exporter CSV
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExport}
                className="flex items-center gap-2"
              >
                <Download size={16} />
                Exporter JSON
              </Button>
            </div>
          </div>

          {/* File Upload */}
          <div 
            data-testid="import-dropzone"
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) handleFileSelect({ target: { files: [dropped] } });
            }}
          >
            <input
              ref={fileInputRef}
              data-testid="import-file-input"
              type="file"
              accept=".json,.csv,.txt"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload size={40} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium" data-testid="import-file-name">
              {file ? file.name : 'Cliquez ou glissez un fichier ici'}
            </p>
            <p className="text-sm text-gray-400 mt-1">Formats acceptés: JSON, CSV (séparateur ; ou ,)</p>
            <p className="text-xs text-gray-400 mt-2 max-w-lg mx-auto">CSV : une ligne par motorisation (ou une seule ligne par modèle sans motorisation). Colonnes : <span className="font-mono">marque; modele; annees</span> (ex. 2019-2024, 2019- ou 2019,2020) et, facultatif, <span className="font-mono">periode; carburant; motorisation; cylindree; puissance; kw; code_moteur</span>. Les marques inconnues sont créées automatiquement avec leur logo.</p>
          </div>

          {/* Import Mode */}
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Mode d'import :</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="importMode"
                  value="add"
                  checked={importMode === 'add'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="text-orange-500"
                />
                <span className="text-sm">Ajouter / Fusionner</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="importMode"
                  value="replace"
                  checked={importMode === 'replace'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="text-orange-500"
                />
                <span className="text-sm">Remplacer existants</span>
              </label>
            </div>
          </div>

          {/* Preview */}
          {previewData && (
            <div data-testid="import-preview" className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium text-gray-900">Aperçu de l'import</p>
                <span data-testid="import-preview-count" className="text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded">
                  {previewData.count} véhicules · {previewData.enginesCount} motorisations
                </span>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {previewData.vehicles.map((v, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-900">{v.brand}</span>
                    <span className="text-gray-600">{v.model}</span>
                    {v.years && (
                      <span className="text-gray-400 text-xs">
                        ({Array.isArray(v.years) ? `${Math.min(...v.years)}-${Math.max(...v.years)}` : v.years})
                      </span>
                    )}
                  </div>
                ))}
                {previewData.count > 5 && (
                  <p className="text-xs text-gray-400">... et {previewData.count - 5} autres</p>
                )}
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div data-testid="import-result" className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
              result.success ? 'bg-green-50' : 'bg-red-50'
            }`}>
              {result.success ? (
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
              ) : (
                <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              )}
              <div>
                <p className={`font-medium ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                  {result.message}
                </p>
                {result.errors?.length > 0 && (
                  <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
                    {result.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button
            data-testid="import-submit-button"
            onClick={handleImport}
            disabled={!previewData || isImporting}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {isImporting ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Import en cours...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Importer {previewData?.count || 0} véhicules
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VehicleImportModal;
